import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import {
  Button,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { IconArrowRight, IconPlus } from "@tabler/icons-react";
import { LinkCascadePicker } from "@/features/editor/components/link/link-cascade-picker.tsx";
import classes from "./api-reference-v3.module.css";

const METHODS = ["get", "post", "put", "patch", "delete"] as const;
type Method = (typeof METHODS)[number];

// Reuse the v1 / public-block badge palette so badges look identical
// across all three API reference variants.
const METHOD_BG: Record<Method, string> = {
  get: "#10b981",
  post: "#3b82f6",
  put: "#f59e0b",
  patch: "#8b5cf6",
  delete: "#ef4444",
};

// Soft circle colors cycle by step index (1→blue, 2→orange, 3→green, …),
// matching the reference design.
const STEP_PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: "#dbeafe", fg: "#1d4ed8" },
  { bg: "#fed7aa", fg: "#c2410c" },
  { bg: "#d1fae5", fg: "#047857" },
  { bg: "#ede9fe", fg: "#6d28d9" },
  { bg: "#fce7f3", fg: "#be185d" },
];

function normalizeMethod(v: unknown): Method {
  if (typeof v !== "string") return "get";
  const lower = v.toLowerCase() as Method;
  return (METHODS as readonly string[]).includes(lower) ? lower : "get";
}

export default function ApiReferenceV3View(props: NodeViewProps) {
  const { node, editor, updateAttributes, selected, getPos } = props;
  const step =
    typeof node.attrs.step === "number" && node.attrs.step > 0
      ? Math.floor(node.attrs.step)
      : 1;
  const title = typeof node.attrs.title === "string" ? node.attrs.title : "";
  const method = normalizeMethod(node.attrs.method);
  const path = typeof node.attrs.path === "string" ? node.attrs.path : "";
  const description =
    typeof node.attrs.description === "string" ? node.attrs.description : "";
  const href = typeof node.attrs.href === "string" ? node.attrs.href : "";
  const canEdit = editor.isEditable;
  const palette = STEP_PALETTE[(step - 1) % STEP_PALETTE.length];

  // Whether the next sibling is also an api-reference-v3 — we only show the
  // "Add step" affordance on the last consecutive step to avoid duplicate
  // buttons when stacking multiple steps.
  const isLastInStack = (() => {
    try {
      const pos =
        typeof getPos === "function" ? (getPos() as number | undefined) : undefined;
      if (typeof pos !== "number") return true;
      const $pos = editor.state.doc.resolve(pos + node.nodeSize);
      const next = $pos.nodeAfter;
      return !next || next.type.name !== "apiReferenceV3";
    } catch {
      return true;
    }
  })();

  const handleAddStep = () => {
    const pos = typeof getPos === "function" ? (getPos() as number | undefined) : undefined;
    if (typeof pos !== "number") return;
    const insertAt = pos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(insertAt, {
        type: "apiReferenceV3",
        attrs: {
          step: step + 1,
          title: "",
          method: "get",
          path: "",
          description: "",
          href: "",
        },
      })
      .run();
  };

  const renderArrow = (
    <IconArrowRight size={18} className={classes.arrow} aria-hidden />
  );

  const cardInner = (
    <div className={classes.card}>
      <span
        className={classes.stepCircle}
        style={{ backgroundColor: palette.bg, color: palette.fg }}
        aria-hidden
      >
        {step}
      </span>

      <div className={classes.body}>
        {canEdit ? (
          <>
            <div className={classes.headerRow}>
              <NumberInput
                size="xs"
                w={70}
                min={1}
                hideControls
                value={step}
                onChange={(v) =>
                  updateAttributes({
                    step:
                      typeof v === "number" ? v : parseInt(String(v), 10) || 1,
                  })
                }
              />
              <TextInput
                size="sm"
                className={classes.title}
                placeholder="Step title (e.g. Complete information)"
                value={title}
                onChange={(e) =>
                  updateAttributes({ title: e.currentTarget.value })
                }
                variant="unstyled"
                style={{ flex: 1, minWidth: 120 }}
              />
            </div>
            <div className={classes.editRow}>
              <Select
                size="xs"
                w={110}
                value={method}
                data={METHODS.map((m) => ({
                  value: m,
                  label: m.toUpperCase(),
                }))}
                onChange={(v) =>
                  updateAttributes({ method: normalizeMethod(v) })
                }
                styles={{
                  input: {
                    backgroundColor: METHOD_BG[method],
                    color: "#fff",
                    fontWeight: 700,
                    border: "none",
                  },
                }}
              />
              <TextInput
                size="xs"
                className={classes.path}
                placeholder="/order/calc"
                value={path}
                onChange={(e) =>
                  updateAttributes({ path: e.currentTarget.value })
                }
                styles={{
                  input: { fontFamily: "ui-monospace, monospace" },
                }}
              />
            </div>
            <Textarea
              size="sm"
              className={classes.description}
              placeholder="Short description of what this step does."
              value={description}
              autosize
              minRows={1}
              onChange={(e) =>
                updateAttributes({ description: e.currentTarget.value })
              }
              variant="unstyled"
            />
            <div className={classes.linkInput}>
              <LinkCascadePicker
                inputSize="xs"
                placeholder="Link (optional) — paste URL or search pages…"
                value={href}
                onChange={(v) => updateAttributes({ href: v })}
              />
            </div>
          </>
        ) : (
          <>
            <div className={classes.headerRow}>
              {title && <span className={classes.title}>{title}</span>}
              <span
                className={classes.badge}
                style={{ backgroundColor: METHOD_BG[method] }}
              >
                {method.toUpperCase()}
              </span>
              {path && <span className={classes.path}>{path}</span>}
            </div>
            {description && (
              <div className={classes.description}>{description}</div>
            )}
          </>
        )}
      </div>

      {renderArrow}
    </div>
  );

  // In read-only mode, wrap the card in an anchor when href is set so the
  // whole row becomes a single clickable link (mirrors public renderer).
  const card =
    !canEdit && href ? (
      <a href={href} className={classes.cardLink}>
        {cardInner}
      </a>
    ) : (
      cardInner
    );

  return (
    <NodeViewWrapper
      className={classes.wrapper}
      data-type="api-reference-v3"
      data-method={method}
      data-step={step}
      data-selected={selected ? "true" : undefined}
    >
      {card}
      {canEdit && isLastInStack && (
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          leftSection={<IconPlus size={14} />}
          className={classes.addStep}
          onClick={handleAddStep}
        >
          Add step
        </Button>
      )}
    </NodeViewWrapper>
  );
}
